// Plaid Link flow that survives an OAuth redirect. OAuth banks (Chase, Wells
// Fargo, Amex, Schwab...) send the user to the bank and back to
// PLAID_REDIRECT_URI (/plaid/oauth); that page resumes Link with the same
// link token and receivedRedirectUri, then finishes exactly like the popup flow.
import { bankFeedsAPI } from './bankFeedsApi';
import { openPlaidLink } from './plaidLink';

const KEY = 'elonest.plaidLink';

export function savePending(state) {
  try { sessionStorage.setItem(KEY, JSON.stringify({ ...state, at: Date.now() })); } catch {}
}

// Link tokens live 4 hours; ignore anything older (or malformed).
export function readPending(now = Date.now()) {
  try {
    const s = JSON.parse(sessionStorage.getItem(KEY) || 'null');
    if (!s || typeof s.token !== 'string' || !s.token || now - Number(s.at) > 4 * 3600 * 1000) return null;
    return s;
  } catch { return null; }
}

// One-shot message carried from /plaid/oauth back to Bank Feeds.
export function setFlash(text) { try { sessionStorage.setItem(`${KEY}.message`, text || ''); } catch {} }
export function takeFlash() {
  try { const t = sessionStorage.getItem(`${KEY}.message`) || ''; sessionStorage.removeItem(`${KEY}.message`); return t; } catch { return ''; }
}

export function clearPending() {
  try { sessionStorage.removeItem(KEY); } catch {}
}

// What the server needs to refuse a duplicate before exchanging.
export function exchangePayload(result, businessId) {
  const accounts = (result.metadata?.accounts || []).map((a) => ({ mask: a.mask, name: a.name, type: a.type, subtype: a.subtype }));
  return {
    public_token: result.publicToken,
    business_id: businessId,
    institution: result.metadata?.institution || {},
    accounts,
  };
}

// Returns a user message, or '' when the user exited Link.
async function finish(state, result) {
  if (!result) return '';
  if (state.kind === 'reauth') {
    await bankFeedsAPI.reauthComplete(state.itemId);
    return 'Bank reconnected.';
  }
  const res = await bankFeedsAPI.exchange(exchangePayload(result, state.businessId));
  if (state.onItem) await state.onItem(res?.data?.item || null);
  return 'Bank connected. Pick the eloNest account for each bank account, then Sync.';
}

// onItem (optional) gets the new connection after the exchange (popup flow
// only; an OAuth redirect finishes on /plaid/oauth and lands on Bank Feeds).
export async function startLink(kind, { businessId = null, itemId = null, onItem = null } = {}) {
  const t = await bankFeedsAPI.linkToken(kind === 'reauth' ? { item_id: itemId } : (businessId ? { business_id: businessId } : {}));
  const state = { kind, businessId, itemId, token: t.data.link_token };
  savePending(state);
  try {
    return await finish({ ...state, onItem }, await openPlaidLink(state.token));
  } finally {
    clearPending();
  }
}

export async function resumeLink(href) {
  const state = readPending();
  if (!state) throw new Error('This bank sign-in has expired. Start again from Bank Feeds.');
  try {
    return await finish(state, await openPlaidLink(state.token, { receivedRedirectUri: href }));
  } finally {
    clearPending();
  }
}
